import React from 'react';

import { Space, Typography, Tag, Popover, Divider } from 'antd';
import { LinkOutlined } from '@ant-design/icons';

import { SegmentationPredictionToCompare } from 'models/predictions';
import { COLORS, TYPOGRAPHY_SIZES } from 'theme';

interface SegmentationDetailsProps {
  segmentationPrediction: SegmentationPredictionToCompare;
  isBlinded: boolean;
  onDicomClick: (dicomIcid: string) => void;
}

const SegmentationDetails: React.FC<SegmentationDetailsProps> = ({
  segmentationPrediction,
  isBlinded,
  onDicomClick,
}) => {
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%', minWidth: '280px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography.Text
          style={{
            fontSize: TYPOGRAPHY_SIZES.NORMAL,
            color: '#8c8c8c',
          }}
        >
          Study ICID:
        </Typography.Text>
        <Tag color="default">{segmentationPrediction?.study?.icid || ''}</Tag>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography.Text
          style={{
            fontSize: TYPOGRAPHY_SIZES.NORMAL,
            color: '#8c8c8c',
          }}
        >
          Study FID:
        </Typography.Text>
        <Tag color="default">{segmentationPrediction?.study?.foreignId || ''}</Tag>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography.Text
          style={{
            fontSize: TYPOGRAPHY_SIZES.NORMAL,
            color: '#8c8c8c',
          }}
        >
          DICOM ICID:
        </Typography.Text>
        <Popover content="Click to view this DICOM on Atlas" trigger="hover">
          <Tag
            color="default"
            style={{ cursor: segmentationPrediction?.dicom?.icid ? 'pointer' : 'default' }}
            onClick={() =>
              segmentationPrediction?.dicom?.icid && onDicomClick(segmentationPrediction.dicom.icid)
            }
          >
            {segmentationPrediction?.dicom?.icid || ''} <LinkOutlined />
          </Tag>
        </Popover>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography.Text
          style={{
            fontSize: TYPOGRAPHY_SIZES.NORMAL,
            color: '#8c8c8c',
          }}
        >
          DICOM FID:
        </Typography.Text>
        <Tag color="default">{segmentationPrediction?.dicom?.foreignId || ''}</Tag>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography.Text
          style={{
            fontSize: TYPOGRAPHY_SIZES.NORMAL,
            color: '#8c8c8c',
          }}
        >
          Pipeline UUID:
        </Typography.Text>
        <Tag color="default">
          {segmentationPrediction.prediction?.source_uuid ||
            segmentationPrediction.prediction?.sourceUuid ||
            (segmentationPrediction as any).source_uuid ||
            (segmentationPrediction as any).sourceUuid ||
            ''}
        </Tag>
      </div>

      <Divider style={{ margin: '8px 0' }} />

      {isBlinded ? (
        <>
          {/* Blinded Mode - Segmentation A */}
          <div style={{ marginBottom: '8px' }}>
            <Typography.Text
              style={{
                fontSize: TYPOGRAPHY_SIZES.NORMAL,
                color: COLORS.lightDark,
                fontWeight: 500,
              }}
            >
              Segmentation A
            </Typography.Text>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography.Text
              style={{
                fontSize: TYPOGRAPHY_SIZES.SMALL,
                color: '#8c8c8c',
              }}
            >
              ICID:
            </Typography.Text>
            <Tag color="blue">{segmentationPrediction.segmentationA?.icid || ''}</Tag>
          </div>

          <Divider style={{ margin: '8px 0' }} />

          {/* Blinded Mode - Segmentation B */}
          <div style={{ marginBottom: '8px' }}>
            <Typography.Text
              style={{
                fontSize: TYPOGRAPHY_SIZES.NORMAL,
                color: COLORS.lightDark,
                fontWeight: 500,
              }}
            >
              Segmentation B
            </Typography.Text>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography.Text
              style={{
                fontSize: TYPOGRAPHY_SIZES.SMALL,
                color: '#8c8c8c',
              }}
            >
              ICID:
            </Typography.Text>
            <Tag color="orange">{segmentationPrediction.segmentationB?.icid || ''}</Tag>
          </div>
        </>
      ) : (
        <>
          {/* Non-blinded Mode - Ground Truth Section */}
          <div style={{ marginBottom: '8px' }}>
            <Typography.Text
              style={{
                fontSize: TYPOGRAPHY_SIZES.NORMAL,
                color: COLORS.lightDark,
                fontWeight: 500,
              }}
            >
              Ground truth segmentation
            </Typography.Text>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography.Text
              style={{
                fontSize: TYPOGRAPHY_SIZES.SMALL,
                color: '#8c8c8c',
              }}
            >
              ICID:
            </Typography.Text>
            <Tag color="green">{segmentationPrediction.label?.icid || ''}</Tag>
          </div>

          <Divider style={{ margin: '8px 0' }} />

          {/* Non-blinded Mode - AI Prediction Section */}
          <div style={{ marginBottom: '8px' }}>
            <Typography.Text
              style={{
                fontSize: TYPOGRAPHY_SIZES.NORMAL,
                color: COLORS.lightDark,
                fontWeight: 500,
              }}
            >
              AI prediction segmentation
            </Typography.Text>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography.Text
              style={{
                fontSize: TYPOGRAPHY_SIZES.SMALL,
                color: '#8c8c8c',
              }}
            >
              ICID:
            </Typography.Text>
            <Tag color="blue">{segmentationPrediction.prediction?.icid || ''}</Tag>
          </div>
        </>
      )}
    </Space>
  );
};

export default SegmentationDetails;
